import { z } from 'zod';
// ============================================================================
// 1. Modos de Control de Acceso B2C (Marcha Blanca -> Suscripción con Trial)
// ============================================================================
export const EntitlementModeSchema = z.enum([
    'SOFT_LAUNCH_ALLOWLIST',
    'SUBSCRIPTION_WITH_TRIAL',
]);
export const SubscriptionStatusSchema = z.enum([
    'PILOT_UNLIMITED',
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'CANCELED',
    'EXPIRED',
]);
// ============================================================================
// 2. Ingresos Recurrentes, No Recurrentes y Políticas de Límite de Gasto (%)
// ============================================================================
export const InflowFrequencySchema = z.enum([
    'MONTHLY',
    'BIWEEKLY',
    'WEEKLY',
    'CUSTOM_DAYS',
]);
/**
 * Define cómo un ingreso NO recurrente afecta al Límite de Gasto del período:
 * - USE_CYCLE_RATE: Aplica el mismo % configurado para el ciclo actual.
 * - CUSTOM_RATE: Aplica un % específico indicado para este ingreso extraordinario.
 * - RESERVE_ONLY: No incrementa el límite de gasto (100% ahorro/reserva).
 */
export const WindfallAllocationModeSchema = z.enum([
    'USE_CYCLE_RATE',
    'CUSTOM_RATE',
    'RESERVE_ONLY',
]);
export const ExpenseCategorySchema = z.enum([
    'FOOD_DINING',
    'GROCERIES',
    'TRANSPORT',
    'UTILITIES',
    'HOUSING',
    'HEALTH',
    'ENTERTAINMENT',
    'SHOPPING',
    'EDUCATION',
    'FINANCIAL',
    'OTHER',
]);
export const ExpenseSourceSchema = z.enum([
    'WEB_MANUAL',
    'RECEIPT_OCR',
    'PUBLIC_API',
    'RECURRING_RULE',
]);
export const MAX_USER_CREDIT_CARDS = 3;
export const RecurringValidityModeSchema = z.enum([
    'INDEFINITE',
    'EXPIRES_ON_DATE',
]);
export const PaymentMethodTypeSchema = z.enum([
    'CREDIT_CARD',
    'DIRECT_DEBIT_OR_CASH',
]);
export const BillingCycleHorizonSchema = z.enum([
    'CURRENT_CYCLE',
    'NEXT_CYCLE',
]);
export const CreditCardProfileSchema = z.object({
    id: z.string().min(1).max(80),
    alias: z.string().min(1).max(80),
    issuerBank: z.string().max(80).optional().default(''),
    lastFourDigits: z.string().max(4).optional().default(''),
    colorHex: z.string().max(16).optional().default('#10b981'),
    billingCloseDayOfMonth: z.number().int().min(1).max(31),
    paymentDueDayOfMonth: z.number().int().min(1).max(31),
    monthlyLimitCents: z.number().int().nonnegative().nullable().optional(),
    isActive: z.boolean().default(true),
});
export const CreateCreditCardRequestSchema = z.object({
    id: z.string().min(1).max(80).optional(),
    alias: z.string().min(1).max(80),
    issuerBank: z.string().max(80).optional().default(''),
    lastFourDigits: z.string().max(4).optional().default(''),
    colorHex: z.string().max(16).optional().default('#10b981'),
    billingCloseDayOfMonth: z.number().int().min(1).max(31),
    paymentDueDayOfMonth: z.number().int().min(1).max(31),
    monthlyLimitSoles: z.number().nonnegative().nullable().optional(),
    monthlyLimitCents: z.number().int().nonnegative().nullable().optional(),
    isActive: z.boolean().default(true),
});
export const UpdateCreditCardRequestSchema = z.object({
    alias: z.string().min(1).max(80).optional(),
    issuerBank: z.string().max(80).optional(),
    lastFourDigits: z.string().max(4).optional(),
    colorHex: z.string().max(16).optional(),
    billingCloseDayOfMonth: z.number().int().min(1).max(31).optional(),
    paymentDueDayOfMonth: z.number().int().min(1).max(31).optional(),
    monthlyLimitSoles: z.number().nonnegative().nullable().optional(),
    monthlyLimitCents: z.number().int().nonnegative().nullable().optional(),
    isActive: z.boolean().optional(),
});
export const ExpenseLineItemSchema = z.object({
    description: z.string().min(1).max(200),
    quantity: z.number().positive(),
    totalCents: z.number().int().nonnegative(),
});
// ============================================================================
// 3. Esquemas de Entrada (Comandos API REST v1)
// ============================================================================
export const RegisterUserRequestSchema = z.object({
    email: z.string().email(),
    fullName: z.string().min(2).max(120),
    password: z.string().min(8).max(128),
    defaultSpendingRatePercent: z.number().min(1).max(100).default(70),
    keepRateConstantAcrossPeriods: z.boolean().default(true),
    nextRenewalDateIso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    initialRecurringIncomeSoles: z.number().min(0).default(0),
});
export const SignupUserRequestSchema = z.object({
    fullName: z.string().min(2).max(120),
    email: z.string().email(),
    phone: z.string().max(30).optional(),
    occupation: z.string().max(80).optional(),
    appBaseUrl: z.string().url().optional(),
});
export const SetPasswordRequestSchema = z.object({
    email: z.string().email(),
    token: z.string().min(8),
    password: z.string().min(8).max(128),
});
export const LoginUserRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const UpdateCyclePolicyRequestSchema = z.object({
    spendingRatePercent: z.number().min(1).max(100),
    keepRateConstantAcrossPeriods: z.boolean(),
    startDateIso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    renewalDateIso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const CreateRecurringInflowRequestSchema = z.object({
    name: z.string().min(1).max(120),
    amountSoles: z.number().positive(),
    frequency: InflowFrequencySchema,
    customIntervalDays: z.number().int().min(1).max(365).optional(),
    renewalDayOfMonth: z.number().int().min(1).max(31).default(30),
    secondBillingDayOfMonth: z.number().int().min(1).max(31).optional(),
    occurrencesPerCycle: z.number().int().min(1).max(31).default(1),
    isPrimaryCycleAnchor: z.boolean().default(true),
    autoUpdateCycleRenewalDate: z.boolean().default(false),
});
export const UpdateRecurringInflowRequestSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    amountSoles: z.number().positive().optional(),
    frequency: InflowFrequencySchema.optional(),
    customIntervalDays: z.number().int().min(1).max(365).optional(),
    renewalDayOfMonth: z.number().int().min(1).max(31).optional(),
    secondBillingDayOfMonth: z.number().int().min(1).max(31).optional(),
    occurrencesPerCycle: z.number().int().min(1).max(31).optional(),
    isPrimaryCycleAnchor: z.boolean().optional(),
    isActive: z.boolean().optional(),
});
export const CreateWindfallInflowRequestSchema = z.object({
    description: z.string().min(1).max(160),
    amountSoles: z.number().positive(),
    allocationMode: WindfallAllocationModeSchema,
    customAllocationPercent: z.number().min(0).max(100).optional(),
    receivedAtIso: z.string().optional(),
});
export const UpdateWindfallInflowRequestSchema = z.object({
    description: z.string().min(1).max(160).optional(),
    amountSoles: z.number().positive().optional(),
    allocationMode: WindfallAllocationModeSchema.optional(),
    customAllocationPercent: z.number().min(0).max(100).optional(),
    receivedAtIso: z.string().optional(),
});
export const CreateExpenseRequestSchema = z.object({
    amountSoles: z.number().positive(),
    category: ExpenseCategorySchema,
    merchantName: z.string().min(1).max(140),
    issuerRuc: z.string().max(20).nullable().optional(),
    receiptNumber: z.string().max(60).nullable().optional(),
    description: z.string().max(280).optional(),
    occurredAtIso: z.string().optional(),
    receiptScanId: z.string().uuid().optional(),
    r2ReceiptKey: z.string().max(500).nullable().optional(),
    receiptImageDataUrl: z.string().nullable().optional(),
    recurringRuleId: z.string().optional(),
    isInevitable: z.boolean().optional(),
    paymentMethod: PaymentMethodTypeSchema.optional(),
    creditCardId: z.string().max(80).nullable().optional(),
    cardLastFourDigits: z.string().max(4).nullable().optional(),
    billingCycleHorizon: BillingCycleHorizonSchema.optional(),
    lineItems: z.array(ExpenseLineItemSchema).optional(),
});
export const UpdateExpenseRequestSchema = z.object({
    amountSoles: z.number().positive().optional(),
    category: ExpenseCategorySchema.optional(),
    merchantName: z.string().min(1).max(140).optional(),
    issuerRuc: z.string().max(20).nullable().optional(),
    receiptNumber: z.string().max(60).nullable().optional(),
    description: z.string().max(280).optional(),
    occurredAtIso: z.string().optional(),
    isInevitable: z.boolean().optional(),
    paymentMethod: PaymentMethodTypeSchema.optional(),
    creditCardId: z.string().max(80).nullable().optional(),
    cardLastFourDigits: z.string().max(4).nullable().optional(),
    billingCycleHorizon: BillingCycleHorizonSchema.optional(),
    r2ReceiptKey: z.string().max(500).nullable().optional(),
    receiptImageDataUrl: z.string().nullable().optional(),
    lineItems: z.array(ExpenseLineItemSchema).optional(),
});
export const CreateRecurringExpenseRequestSchema = z.object({
    name: z.string().min(1).max(120),
    merchantName: z.string().min(1).max(140).optional(),
    issuerRuc: z.string().max(20).nullable().optional(),
    category: ExpenseCategorySchema,
    frequency: InflowFrequencySchema.default('MONTHLY'),
    customIntervalDays: z.number().int().min(1).max(365).optional(),
    defaultAmountSoles: z.number().positive(),
    billingDayOfMonth: z.number().int().min(1).max(31).default(15),
    isInevitable: z.boolean().default(true),
    validityMode: RecurringValidityModeSchema.default('INDEFINITE'),
    expirationDateIso: z.string().nullable().optional(),
    isCreditCardExpense: z.boolean().default(false),
    creditCardId: z.string().max(80).nullable().optional(),
});
export const UpdateRecurringExpenseRequestSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    merchantName: z.string().min(1).max(140).optional(),
    issuerRuc: z.string().max(20).nullable().optional(),
    category: ExpenseCategorySchema.optional(),
    frequency: InflowFrequencySchema.optional(),
    customIntervalDays: z.number().int().min(1).max(365).optional(),
    defaultAmountSoles: z.number().positive().optional(),
    billingDayOfMonth: z.number().int().min(1).max(31).optional(),
    isInevitable: z.boolean().optional(),
    validityMode: RecurringValidityModeSchema.optional(),
    expirationDateIso: z.string().nullable().optional(),
    isCreditCardExpense: z.boolean().optional(),
    creditCardId: z.string().max(80).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const UpdateRecurringExpenseMonthRequestSchema = z.object({
    monthIso: z.string().regex(/^\d{4}-\d{2}$/),
    amountSoles: z.number().nonnegative(),
    applyAsNewDefault: z.boolean().default(false),
    clearMonthOverride: z.boolean().optional(),
    isInevitable: z.boolean().optional(),
});
// ============================================================================
// 4. Esquema de Extracción Inteligente de Boletas (Receipt OCR / Vision AI)
// ============================================================================
export const ReceiptDocumentTypeSchema = z.enum([
    'BOLETA_ELECTRONICA',
    'FACTURA_ELECTRONICA',
    'TICKET_POS',
    'YAPE_PLIN_VOUCHER',
    'BANK_APP_SCREENSHOT',
    'UNKNOWN',
]);
export const ReceiptExtractionResultSchema = z.object({
    scanId: z.string(),
    r2ObjectKey: z.string(),
    documentType: ReceiptDocumentTypeSchema,
    totalAmountCents: z.number().int().nonnegative(),
    totalAmountSoles: z.number().nonnegative(),
    taxAmountCents: z.number().int().nonnegative().nullable(),
    currency: z.enum(['PEN', 'USD']),
    merchantName: z.string(),
    issuerRuc: z.string().nullable(),
    receiptNumber: z.string().nullable(),
    issuedAtIso: z.string(),
    cardLastFourDigits: z
        .string()
        .regex(/^\d{4}$/)
        .nullable()
        .optional()
        .default(null),
    suggestedCategory: ExpenseCategorySchema,
    confidencePercent: z.number().min(0).max(100),
    lineItems: z.array(z.object({
        description: z.string(),
        quantity: z.number().positive(),
        totalCents: z.number().int().nonnegative(),
    })),
});
// ============================================================================
// 5. Contrato de Telemetría y KPIs del Impacto Temporal del Sol (KPI-01..07)
// ============================================================================
export const BurnVelocityStatusSchema = z.enum([
    'OPTIMAL',
    'ON_TRACK',
    'WARNING',
    'CRITICAL',
]);
//# sourceMappingURL=index.js.map